import apiFetch from '../../common/apiFetch';
import { logError, base64ToBlob, getImageDimensions } from './utils';
import { ImageResult } from './eraseContent';

// Define supported aspect ratios
export type AspectRatio = '16:9' | '1:1' | '21:9' | '2:3' | '3:2' | '4:5' | '5:4' | '9:16' | '9:21';
export type GenerationMode = 'structure' | 'style';

// Export the GenerationResult type (same as ImageResult but named differently)
export type GenerationResult = ImageResult;

export interface GenerateWithControlOptions {
	image: string // base64 string
	prompt: string
	mode: GenerationMode
	// Common optional parameters
	negative_prompt?: string
	output_format?: 'png'
	style_preset?: 'photographic'
	// Structure-specific parameters
	control_strength?: number
	// Style-specific parameters
	aspect_ratio?: AspectRatio
	fidelity?: number
	// Processing options
	processTo1200x628?: boolean
}

interface ImageResponse {
	image?: string
	[key: string]: unknown
}

export const generateWithControl = async (options: GenerateWithControlOptions): Promise<GenerationResult> => {
	let objectUrl: string | null = null;

	try {
		const response = await apiFetch('/ai-image-editor/generate-with-control', {
			method: 'POST',
			headers: {
				contentType: 'application/json',
				accept: 'application/json'
			},
			body: JSON.stringify({
				image: options.image,
				prompt: options.prompt,
				control_type: options.mode,
				negative_prompt: options.negative_prompt,
				output_format: options.output_format ?? 'png',
				style_preset: options.style_preset ?? 'photographic',
				// Structure-specific parameters
				control_strength: options.mode === 'structure' ? (options.control_strength ?? 0.7) : undefined,
				// Style-specific parameters
				aspect_ratio: options.mode === 'style' ? (options.aspect_ratio ?? '1:1') : undefined,
				fidelity: options.mode === 'style' ? (options.fidelity ?? 0.7) : undefined,
				// Processing options
				processTo1200x628: options.mode === 'style' ? (options.processTo1200x628 ?? true) : undefined
			})
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`Failed to generate image: ${response.status} ${response.statusText} - ${errorText}`);
		}

		// Check if response is JSON or binary
		const contentType = response.headers.get('content-type');
		let resultBlob: Blob;

		if (contentType && contentType.includes('application/json')) {
			// Handle JSON response with proper typing
			const result = await response.json() as ImageResponse;
			if (!result.image) {
				throw new Error('No image data received from server');
			}

			// Convert base64 to blob
			try {
				resultBlob = base64ToBlob(result.image);
			} catch (error) {
				logError('Error converting base64 to blob:', error);
				throw new Error('Failed to process image data from server');
			}
		} else {
			// Handle binary response
			resultBlob = await response.blob();
		}

		objectUrl = URL.createObjectURL(resultBlob);

		// Get dimensions from the generated image
		const dimensions = await getImageDimensions(objectUrl);

		return {
			url: objectUrl,
			width: dimensions.width,
			height: dimensions.height,
			cleanup: () => {
				if (objectUrl) {
					URL.revokeObjectURL(objectUrl);
					objectUrl = null;
				}
			}
		};
	} catch (error) {
		logError('Error in generateWithControl:', error);
		if (objectUrl) {
			URL.revokeObjectURL(objectUrl);
		}
		throw error;
	}
}
